import React, { useState, useMemo } from 'react';
import { css } from '@emotion/css';
import { Table, SpinningLoader, Button, Chip } from '@frontend/design-system';
import { useListSandboxesQuery } from '../queries/sales-demo.queries';
import type { Sandbox } from '../types/sales-demo.types';

interface SandboxListProps {
  onSelectSandbox: (sandboxId: string) => void;
}

type SandboxRow = {
  name: string;
  id: string;
  owner: string;
  type: string;
  datasets: number;
  datasetNames: string[];
  sandboxId: string;
};

const PAGE_SIZE = 25;

const colConfig = (onSelectSandbox: (id: string) => void) => [
  {
    id: 'name',
    Header: 'Name',
    accessor: (row: SandboxRow) => row.name,
    cellRenderer: (value: string) =>
      value ? (
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>{value}</span>
      ) : (
        <span style={{ fontSize: '12px', color: '#8E959E' }}>-</span>
      ),
  },
  {
    id: 'id',
    Header: 'Sandbox ID',
    accessor: (row: SandboxRow) => row.id,
    cellRenderer: (value: string) => (
      <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#333' }}>{value}</span>
    ),
  },
  {
    id: 'owner',
    Header: 'Owner',
    accessor: (row: SandboxRow) => row.owner,
    cellRenderer: (value: string) => (
      <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#5F6771' }}>
        {value ? `${value.substring(0, 8)}...` : '-'}
      </span>
    ),
  },
  {
    id: 'type',
    Header: 'Type',
    accessor: (row: SandboxRow) => row.type,
    cellRenderer: (value: string) => (
      <Chip variant={value === 'Ephemeral' ? 'primary' : 'eggplant'} size='tiny'>
        {value}
      </Chip>
    ),
  },
  {
    id: 'datasets',
    Header: 'Datasets',
    accessor: (row: SandboxRow) => row,
    cellRenderer: (row: SandboxRow) => (
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' as const }}>
        {row.datasetNames.length > 0 ? (
          row.datasetNames.map((name, i) => (
            <Chip key={i} variant='neutral' size='tiny'>
              {name}
            </Chip>
          ))
        ) : (
          <span style={{ fontSize: '12px', color: '#8E959E' }}>None</span>
        )}
      </div>
    ),
  },
  {
    id: 'actions',
    Header: '',
    accessor: (row: SandboxRow) => row.sandboxId,
    cellRenderer: (value: string) => (
      <Button variant='tertiary' size='small' onClick={() => onSelectSandbox(value)}>
        View Details
      </Button>
    ),
  },
];

function toRow(sandbox: Sandbox): SandboxRow {
  const ephId =
    (sandbox as any).ephemeralId ??
    (sandbox as any).ephemeral_id ??
    (sandbox as any).config?.ephemeralId ??
    (sandbox as any).config?.ephemeral_id;
  const owner =
    (sandbox as any).ownerUserId ??
    (sandbox as any).owner_user_id ??
    (sandbox as any).config?.ownerUserId ??
    (sandbox as any).config?.owner_user_id ??
    '';
  const datasets = (sandbox as any).datasets ?? [];
  const cfg = (sandbox as any).config ?? {};
  // display_name is optional and often unset; fall back to the integration/vertical
  // so the Name column always identifies what the sandbox was seeded for.
  const name = cfg.displayName ?? cfg.display_name ?? cfg.integrationName ?? cfg.integration_name ?? cfg.vertical ?? '';
  return {
    name,
    id: sandbox.id ?? 'Unknown',
    owner,
    type: ephId ? 'Ephemeral' : 'Persistent',
    datasets: datasets.length,
    datasetNames: datasets.map((d: any) => {
      const url = d.typeUrl ?? d.type_url ?? '';
      return url.split('/').pop() || 'unknown';
    }),
    sandboxId: sandbox.id ?? '',
  };
}

export const SandboxList: React.FC<SandboxListProps> = ({ onSelectSandbox }) => {
  const [pageToken, setPageToken] = useState('');
  const [pageHistory, setPageHistory] = useState<string[]>(['']);

  const {
    data: sandboxesData,
    isLoading,
    isFetching,
    refetch,
  } = useListSandboxesQuery({
    pageSize: PAGE_SIZE,
    pageToken,
    filter: '',
  });

  const sandboxes = sandboxesData?.sandboxes ?? [];
  const nextPageToken = (sandboxesData as any)?.nextPageToken ?? (sandboxesData as any)?.next_page_token ?? '';
  const currentPageIndex = pageHistory.indexOf(pageToken);
  const hasNextPage = !!nextPageToken;
  const hasPrevPage = currentPageIndex > 0;

  const typeCounts = useMemo(() => {
    const ephemeral = sandboxes.filter((s: Sandbox) => {
      const a = s as any;
      return a.ephemeralId || a.ephemeral_id || a.config?.ephemeralId || a.config?.ephemeral_id;
    }).length;
    return { ephemeral, persistent: sandboxes.length - ephemeral };
  }, [sandboxes]);

  const tableData = useMemo(() => sandboxes.map(toRow), [sandboxes]);
  const columns = useMemo(() => colConfig(onSelectSandbox), [onSelectSandbox]);

  const goNextPage = () => {
    if (!nextPageToken) return;
    setPageHistory((prev) => {
      const next = [...prev];
      if (!next.includes(nextPageToken)) next.push(nextPageToken);
      return next;
    });
    setPageToken(nextPageToken);
  };

  const goPrevPage = () => {
    if (currentPageIndex <= 0) return;
    setPageToken(pageHistory[currentPageIndex - 1]);
  };

  if (isLoading) {
    return (
      <div
        className={css({
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
        })}
      >
        <SpinningLoader />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {typeCounts.persistent > 0 && (
              <Chip variant='eggplant' size='default'>
                {typeCounts.persistent} persistent
              </Chip>
            )}
            {typeCounts.ephemeral > 0 && (
              <Chip variant='primary' size='default'>
                {typeCounts.ephemeral} ephemeral
              </Chip>
            )}
          </div>
          <div style={{ height: '20px', width: '1px', backgroundColor: '#A1A8B0' }} />
          <span style={{ fontSize: '12px', color: '#6F7780' }}>
            Page {currentPageIndex + 1} · {sandboxes.length} sandbox{sandboxes.length !== 1 ? 'es' : ''}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button variant='secondary' size='small' iconName='arrow-left' onClick={goPrevPage} disabled={!hasPrevPage}>
            Prev
          </Button>
          <Button variant='secondary' size='small' iconName='arrow-right' onClick={goNextPage} disabled={!hasNextPage}>
            Next
          </Button>
          <div style={{ height: '20px', width: '1px', backgroundColor: '#A1A8B0' }} />
          <Button variant='secondary' size='small' iconName='refresh' onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </div>

      <div
        style={{
          borderRadius: '8px',
          border: `1px solid #A1A8B0`,
          overflow: 'hidden',
          opacity: isFetching ? 0.6 : 1,
          transition: 'opacity 0.15s',
        }}
      >
        {sandboxes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6F7780' }}>
            <div style={{ fontSize: '14px' }}>No sandboxes found</div>
          </div>
        ) : (
          <Table colConfig={columns} data={tableData} />
        )}
      </div>
    </div>
  );
};
